import { useLocation, useSearchParams } from "react-router-dom";
import { IOptionExtended, IOptionNote } from "../../../../models/Configuration";
import { Box, Button, Checkbox, FormControlLabel, FormHelperText, Grid, IconButton, InputLabel, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { MainCard } from "../../../../components/surface/MainCard";
import { SectionBox } from "../../../../components/surface/SectionBox";
import { StackLabel } from "../../../../components/label/StackLabel";
import { useEffect, useState } from "react";
import BackButton from "../../../../components/button/BackButton";
import { FieldArray, Formik, FormikErrors } from "formik";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import MainModal from "../../../../components/modal/MainModal";
import ConfirmationBox from "../../../../components/modal/ConfirmationBox";
import { apiStore } from "../../../../api/ApiFactory";
import { renderLinkedText } from "../../../../helpers/utils/linkify";
import { useNotification } from "../../../../store/context/NotificationContext";

interface IProps {
    data?: IOptionExtended
}

const NoteSchema = Yup.object().shape({
    value: Yup.string().trim().required("Value is required"),
    sortOrder: Yup.number()
        .transform((value, original) => (original === "" || original === null ? null : value))
        .typeError("Sort Order must be a number")
        .nullable()
});

const ValidationSchema = Yup.object().shape({
    notes: Yup.array().of(NoteSchema)
});

//A 400 on a POST is rejected with the validation array itself, other verbs keep the axios error.
const getErrorMessage = (error: any): string => {
    const errors = Array.isArray(error) ? error : error?.response?.data;
    return Array.isArray(errors) && errors.length > 0
        ? errors[0].errorMessage ?? "Unexpected Error"
        : "Unexpected Error";
}

const ManageOptionNotes = () => {

    const location = useLocation();
    const state: any = location.state;
    let { data }: IProps = state ?? {};

    const [searchParams] = useSearchParams();
    const optionId = searchParams.get('id');

    const { Toast } = useNotification();

    const [option, setOption] = useState<IOptionExtended | undefined>(data);
    const [busyIndex, setBusyIndex] = useState<number | null>(null);
    const [pendingDelete, setPendingDelete] = useState<{ index: number, note: IOptionNote } | null>(null);
    const [pendingGlobal, setPendingGlobal] = useState<{ index: number, note: IOptionNote, next: boolean } | null>(null);

    useEffect(() => {
        if (optionId != null && option === undefined) {
            apiStore.boatConfigApi.getOptionDetails(optionId)
                .then((response) => setOption(response))
                .catch(() => Toast.error({ title: "Unable to load option notes." }));
        }
    }, [optionId]);

    const getEmptyNote = (): IOptionNote => ({
        id: null,
        header: "",
        value: "",
        isGlobal: false,
        boatOptionId: option ? Number(option.id) : null,
        optionName: option?.name ?? "",
        sortOrder: null,
        revision: option?.year ?? ""
    });

    const getRevision = (note: IOptionNote) => note.revision || option?.year || "";

    if (option === undefined) {
        return <>{"Loading..."}</>
    }

    return (
        <>
            <Stack direction="row" width="100%" justifyContent="space-between" spacing={2} mb={2}>
                <Typography className="page-header-label">
                        {"Option Notes"}
                </Typography>
                <BackButton />
            </Stack>
            <Grid container spacing={4}>
                <Grid item xs={12} lg={4}>
                    <MainCard title="GENERAL INFORMATION" divider={true}>
                        <SectionBox>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <MainCard subcard title="Details">
                                        <SectionBox>
                                            <StackLabel title='Series' label={option.seriesName ?? ""} />
                                            <StackLabel title='Model' label={option.modelName ?? ""} />
                                            <StackLabel title='Model Year' label={option.year ?? ""} />
                                            <StackLabel title='Option' label={option.name ?? ""} />
                                        </SectionBox>
                                    </MainCard>
                                </Grid>
                                <Grid item xs={12} sx={{display:"flex", justifyContent:"center"}}>
                                    <Typography className='info-emphasis-lg' component="span" >
                                        {"Fields configured here will display at the bottom of the section in the designer."}
                                    </Typography>
                                   
                                </Grid>
                                <Grid item xs={12} sx={{display:"flex", justifyContent:"center"}}>
                                    <Typography className='info-emphasis-lg' component="span">
                                        {"If marked as global, the field will be applied across all models for this model year that have this option."}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </SectionBox>
                    </MainCard>
                </Grid>
                <Grid item xs={12} lg={8}>
                    <MainCard title="NOTE CONFIGURATION" divider={true}>
                        <SectionBox>
                            <Formik
                                enableReinitialize
                                initialValues={{ notes: option.notes ?? [] }}
                                validationSchema={ValidationSchema}
                                onSubmit={() => {}}
                            >
                                {({ values, errors, touched, handleChange, setFieldValue, setFieldTouched }) => {

                                    const saveNote = async (index: number) => {
                                        const note = values.notes[index];

                                        setFieldTouched(`notes.${index}.value`, true);
                                        setFieldTouched(`notes.${index}.sortOrder`, true);

                                        if (!(await NoteSchema.isValid(note))) {
                                            return;
                                        }

                                        const sortOrder = note.sortOrder === null || note.sortOrder === undefined || String(note.sortOrder) === ""
                                            ? null
                                            : Number(note.sortOrder);

                                        const payload: IOptionNote = {
                                            id: note.id,
                                            header: note.header ?? "",
                                            value: note.value,
                                            isGlobal: note.isGlobal,
                                            boatOptionId: note.isGlobal ? null : Number(option.id),
                                            optionName: note.optionName || option.name,
                                            sortOrder: sortOrder,
                                            revision: note.revision || option.year
                                        };

                                        setBusyIndex(index);
                                        try {
                                            const saved = await apiStore.boatConfigApi.saveOptionNote(payload);
                                            setFieldValue(`notes.${index}`, saved);
                                        }
                                        catch (error) {
                                            Toast.error({ title: getErrorMessage(error) });
                                        }
                                        finally {
                                            setBusyIndex(null);
                                        }
                                    };

                                    return (
                                        <FieldArray name="notes">
                                            {({ remove, push }) => {

                                                const confirmDelete = async () => {
                                                    if (pendingDelete === null) return;

                                                    const { index, note } = pendingDelete;

                                                    setBusyIndex(index);
                                                    try {
                                                        await apiStore.boatConfigApi.deleteOptionNote(note.id!);
                                                        remove(index);
                                                        setPendingDelete(null);
                                                    }
                                                    catch (error) {
                                                        Toast.error({ title: getErrorMessage(error) });
                                                    }
                                                    finally {
                                                        setBusyIndex(null);
                                                    }
                                                };

                                                const requestDelete = (index: number) => {
                                                    const note = values.notes[index];

                                                    if (note.id === null || note.id === undefined) {
                                                        remove(index);
                                                        return;
                                                    }

                                                    setPendingDelete({ index, note });
                                                };

                                                const requestGlobalChange = (index: number, next: boolean) => {
                                                    const note = values.notes[index];

                                                    if (note.id === null || note.id === undefined) {
                                                        setFieldValue(`notes.${index}.isGlobal`, next);
                                                        return;
                                                    }

                                                    setPendingGlobal({ index, note, next });
                                                };

                                                const confirmGlobalChange = () => {
                                                    if (pendingGlobal === null) return;

                                                    setFieldValue(`notes.${pendingGlobal.index}.isGlobal`, pendingGlobal.next);
                                                    setPendingGlobal(null);
                                                };

                                                return (
                                                    <>
                                                        <MainModal open={pendingDelete !== null} handleClose={() => setPendingDelete(null)}>
                                                            <ConfirmationBox
                                                                cancelClick={() => setPendingDelete(null)}
                                                                confirmClick={confirmDelete}
                                                                disable={busyIndex !== null}
                                                                message={pendingDelete?.note.isGlobal
                                                                    ? `This Option Note is set globally and will be removed for all ${getRevision(pendingDelete.note)} models that have this option name. Are you sure you want to delete it?`
                                                                    : "Are you sure you want to delete this note?"}
                                                            />
                                                        </MainModal>

                                                        <MainModal open={pendingGlobal !== null} handleClose={() => setPendingGlobal(null)}>
                                                            <ConfirmationBox
                                                                cancelClick={() => setPendingGlobal(null)}
                                                                confirmClick={confirmGlobalChange}
                                                                message={pendingGlobal?.next
                                                                    ? `Are you sure you want to make this note global to all ${pendingGlobal ? getRevision(pendingGlobal.note) : ""} Models that have this option?`
                                                                    : "Are you sure you want to make this note specific to this option?"}
                                                            />
                                                        </MainModal>

                                                        {values.notes.length > 0 && (
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={12}>
                                                                    <InputLabel>Notes</InputLabel>
                                                                </Grid>
                                                                {values.notes.map((note, index) => {

                                                                    const noteErrors = errors.notes?.at(index) as FormikErrors<IOptionNote>;
                                                                    const noteTouched = touched.notes?.at(index);

                                                                    return (
                                                                        <Grid item xs={12} key={note.id ?? `new-${index}`}>
                                                                            <MainCard>
                                                                                <SectionBox title={`Note # ${index + 1}`} divider={true} className="header-panel-color">
                                                                                    <Grid container spacing={2}>
                                                                                        <Grid item xs={12} sm={8}>
                                                                                            <InputLabel>Header (optional)</InputLabel>
                                                                                            <TextField
                                                                                                value={note.header ?? ""}
                                                                                                name={`notes.${index}.header`}
                                                                                                id={`notes.${index}.header`}
                                                                                                fullWidth
                                                                                                size="small"
                                                                                                onChange={handleChange}
                                                                                                placeholder="Header"
                                                                                            />
                                                                                        </Grid>
                                                                                        <Grid item xs={12} sm={4}>
                                                                                            <InputLabel>Sort Order</InputLabel>
                                                                                            <TextField
                                                                                                value={note.sortOrder ?? ""}
                                                                                                name={`notes.${index}.sortOrder`}
                                                                                                id={`notes.${index}.sortOrder`}
                                                                                                fullWidth
                                                                                                size="small"
                                                                                                onBlur={() => setFieldTouched(`notes.${index}.sortOrder`, true)}
                                                                                                onChange={handleChange}
                                                                                                placeholder="Sort Order"
                                                                                                error={Boolean(noteTouched?.sortOrder && noteErrors?.sortOrder)}
                                                                                            />
                                                                                            {noteTouched?.sortOrder && noteErrors?.sortOrder && (
                                                                                                <FormHelperText error id={`notes.${index}.sortOrder-error`}>
                                                                                                    {noteErrors.sortOrder}
                                                                                                </FormHelperText>
                                                                                            )}
                                                                                        </Grid>
                                                                                        <Grid item xs={12}>
                                                                                            <InputLabel>Note *</InputLabel>
                                                                                            <TextField
                                                                                                value={note.value ?? ""}
                                                                                                name={`notes.${index}.value`}
                                                                                                id={`notes.${index}.value`}
                                                                                                fullWidth
                                                                                                multiline
                                                                                                minRows={3}
                                                                                                size="small"
                                                                                                onBlur={() => setFieldTouched(`notes.${index}.value`, true)}
                                                                                                onChange={handleChange}
                                                                                                placeholder="Value *"
                                                                                                error={Boolean(noteTouched?.value && noteErrors?.value)}
                                                                                            />
                                                                                            {noteTouched?.value && noteErrors?.value && (
                                                                                                <FormHelperText error id={`notes.${index}.value-error`}>
                                                                                                    {noteErrors.value}
                                                                                                </FormHelperText>
                                                                                            )}

                                                                                            {note.value && (
                                                                                                <>
                                                                                                    <Box sx={{ mt: 1,  p: 1, border: "1px dashed", borderColor: "divider", borderRadius: 1, backgroundColor: "#FAFAFA" }}>
                                                                                                       
                                                                                                        <Typography variant="subtitle2" className="option-note-header">
                                                                                                            {note.header}
                                                                                                        </Typography>
                                                                                                        <Typography variant="caption" component="div" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                                                                                            {renderLinkedText(note.value)}
                                                                                                        </Typography>
                                                                                                        <Typography variant="caption" component="div" width={"100%"} align="right" color="slategray" >
                                                                                                            (Preview)
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                </>
                                                                                            )}

                                                                                            <FormHelperText>
                                                                                                {"A web address included in the note is turned into clickable link in the designer. Use [label](url) to show link as friendly text."}
                                                                                            </FormHelperText>

                                                                                        </Grid>
                                                                                        <Grid item xs={12}>
                                                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                                                <FormControlLabel
                                                                                                    label="Is Global"
                                                                                                    control={
                                                                                                        <Checkbox
                                                                                                            checked={note.isGlobal}
                                                                                                            onChange={(event) => requestGlobalChange(index, event.target.checked)}
                                                                                                        />
                                                                                                    }
                                                                                                />
                                                                                                <Stack direction="row" spacing={1}>
                                                                                                    <Tooltip title="Save Note">
                                                                                                        <span>
                                                                                                            <IconButton
                                                                                                                color="primary"
                                                                                                                disabled={busyIndex !== null}
                                                                                                                onClick={() => saveNote(index)}
                                                                                                            >
                                                                                                                <SaveIcon />
                                                                                                            </IconButton>
                                                                                                        </span>
                                                                                                    </Tooltip>
                                                                                                    <Tooltip title="Delete Note">
                                                                                                        <span>
                                                                                                            <IconButton
                                                                                                                color="error"
                                                                                                                disabled={busyIndex !== null}
                                                                                                                onClick={() => requestDelete(index)}
                                                                                                            >
                                                                                                                <DeleteIcon />
                                                                                                            </IconButton>
                                                                                                        </span>
                                                                                                    </Tooltip>
                                                                                                </Stack>
                                                                                            </Stack>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                </SectionBox>
                                                                            </MainCard>
                                                                        </Grid>
                                                                    );
                                                                })}
                                                            </Grid>
                                                        )}
                                                        <Stack direction="row" mt={1}>
                                                            <Button onClick={() => push(getEmptyNote())}>Add Note</Button>
                                                        </Stack>
                                                    </>
                                                );
                                            }}
                                        </FieldArray>
                                    );
                                }}
                            </Formik>
                        </SectionBox>
                    </MainCard>
                </Grid>
            </Grid>
            <Stack direction="row" width="100%" justifyContent="flex-end" spacing={2} mt={2}>
                <BackButton />
            </Stack>
        </>
    )
}

export default ManageOptionNotes;